import { filterLeafCategories } from "@/domains/documents/utils/leafCategories";

interface CategoryNode {
  id: number;
  category_name: string;
  parent_category_id: number | null;
}

export interface CategoryAssignmentOption {
  id: number;
  /** Full path from the top-level category, e.g. "Design Documentation › Drawing". */
  label: string;
}

const PATH_SEPARATOR = " › ";

/**
 * The category's name prefixed by every ancestor's, top-level first.
 * Stops at a missing or repeated ancestor rather than looping on bad data.
 */
export function categoryPathLabel(
  categoryId: number,
  byId: ReadonlyMap<number, CategoryNode>
): string | null {
  const names: string[] = [];
  const seen = new Set<number>();
  let current = byId.get(categoryId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.category_name);
    current = current.parent_category_id == null ? undefined : byId.get(current.parent_category_id);
  }
  return names.length > 0 ? names.join(PATH_SEPARATOR) : null;
}

/**
 * Categories a document can be assigned deterministically (file-type mappings).
 *
 * Only leaves qualify: a category with subcategories is a step in the classifier's
 * refinement chain, never a final answer — "Design Documentation" is always refined
 * into Drawing, Specification and so on. Leaves are labelled with their full path so
 * a subcategory like "Drawing" is recognisable, and sorted by that label.
 */
export function buildCategoryAssignmentOptions(
  categories: readonly CategoryNode[]
): CategoryAssignmentOption[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  return filterLeafCategories([...categories])
    .map((c) => ({ id: c.id, label: categoryPathLabel(c.id, byId) ?? c.category_name }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
